import { useRef, useState } from "react"
import { MaskedCredentialInput } from "@/components/shared/masked-credential-input"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useUpdateProVenueInstance } from "@/hooks/use-provenue-instances"
import type { SharedProVenueInstance } from "@/types/provenue-instance"
import { useToast } from "@/hooks/use-toast"

interface EditProVenueInstanceDialogProps {
  instance: SharedProVenueInstance
  onClose: () => void
  onUpdated: (instance: SharedProVenueInstance) => Promise<void>
}

export function EditProVenueInstanceDialog({ instance, onClose, onUpdated }: EditProVenueInstanceDialogProps) {
  const updateInstance = useUpdateProVenueInstance(instance.id)
  const submitting = useRef(false)
  const [isSaving, setIsSaving] = useState(false)
  const [name, setName] = useState(instance.name)
  const [hostName, setHostName] = useState(instance.hostName)
  const [consumerKey, setConsumerKey] = useState("")
  const [consumerSecret, setConsumerSecret] = useState("")
  const [error, setError] = useState<string>()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const { toast } = useToast()
  let validHost = false
  try {
    const url = new URL(hostName.trim())
    validHost = url.protocol === "https:" && Boolean(url.host)
  } catch { /* The form remains disabled for invalid URLs. */ }
  const hostError = hostName.trim() && !validHost ? "Enter an HTTPS URL, such as https://sandbox.example.com" : undefined
  const valid = Boolean(name.trim() && validHost)
  const hasChanges = name.trim() !== instance.name || hostName.trim() !== instance.hostName || Boolean(consumerKey.trim() || consumerSecret.trim())
  const hasDraftChanges = name !== instance.name || hostName !== instance.hostName || Boolean(consumerKey || consumerSecret)
  const discard = () => {
    if (submitting.current) return
    setName(instance.name)
    setHostName(instance.hostName)
    setConsumerKey("")
    setConsumerSecret("")
    setError(undefined)
  }

  const save = async () => {
    if (!valid || !hasChanges || submitting.current) return
    submitting.current = true
    setIsSaving(true)
    setError(undefined)
    try {
      const updated = await updateInstance.trigger({
        name: name.trim(), hostName: hostName.trim(),
        ...(consumerKey.trim() ? { consumerKey } : {}),
        ...(consumerSecret.trim() ? { consumerSecret } : {}),
      })
      setConsumerKey("")
      setConsumerSecret("")
      await onUpdated(updated)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to update instance. Please try again."
      setError(message)
      setShowConfirmation(false)
      toast({ variant: "error", title: "Failed to update ProVenue instance", description: message })
    } finally {
      submitting.current = false
      setIsSaving(false)
    }
  }

  return (
    <>
    <Dialog open onOpenChange={(open) => { if (!open && !submitting.current && !showConfirmation) onClose() }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit ProVenue Instance</DialogTitle>
          <DialogDescription>Changes apply to every Smithers device using this shared instance, including through global defaults. Leave either credential blank to keep its saved value.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); if (valid && hasChanges && !submitting.current) setShowConfirmation(true) }} className="flex flex-col gap-4">
          {error && <Alert variant="destructive"><AlertTitle>Couldn’t update instance</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-edit-name">Name</Label>
            <Input id="provenue-edit-name" value={name} onChange={(event) => setName(event.target.value)} required disabled={isSaving} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-edit-host">Host Name</Label>
            <Input id="provenue-edit-host" value={hostName} onChange={(event) => setHostName(event.target.value)} required disabled={isSaving} aria-invalid={Boolean(hostError)} aria-describedby={hostError ? "provenue-edit-host-error" : undefined} />
            {hostError && <p id="provenue-edit-host-error" className="text-sm text-destructive" role="alert">{hostError}</p>}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-edit-key">Consumer Key</Label>
            <MaskedCredentialInput id="provenue-edit-key" value={consumerKey} onChange={(event) => setConsumerKey(event.target.value)} placeholder="Leave blank to keep current" disabled={isSaving} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-edit-secret">Consumer Secret</Label>
            <MaskedCredentialInput id="provenue-edit-secret" value={consumerSecret} onChange={(event) => setConsumerSecret(event.target.value)} placeholder="Leave blank to keep current" disabled={isSaving} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={discard} disabled={(!hasDraftChanges && !error) || isSaving}>Discard changes</Button>
            <Button type="submit" disabled={!valid || !hasChanges || isSaving}>{isSaving ? "Saving…" : "Save Changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <AlertDialog open={showConfirmation} onOpenChange={(open) => { if (!submitting.current) setShowConfirmation(open) }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Save ProVenue instance changes?</AlertDialogTitle>
          <AlertDialogDescription>These changes affect every device using this shared instance, including devices inheriting it through global defaults.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSaving}>Keep editing</AlertDialogCancel>
          <AlertDialogAction disabled={isSaving} onClick={(event) => { event.preventDefault(); void save() }}>{isSaving ? "Saving…" : "Save changes"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
